import React, { useEffect, useMemo, useState } from "react";
import { Input, Select, message, Form, Upload } from "antd";
import {
  CustomerServiceOutlined,
  CheckCircleOutlined,
  PhoneOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import cache from "utils/cache";
import {
  useCreateTicketMutation,
  useCreateCallbackMutation,
} from "api/supportApi";
import uploadSupportFileToS3, {
  validateSupportFile,
} from "utils/supportS3Upload";
import {
  StyledModal,
  TitleRow,
  Subtitle,
  ModeRow,
  ModeButton,
  FieldLabel,
  PriorityRow,
  ChipButton,
  SlaText,
  SubmitButton,
  AvailabilityBanner,
  SlotRow,
  DoneWrap,
  DoneTitle,
  DoneMsg,
  StyledUpload,
} from "./styles";

const { TextArea } = Input;

const SLAS = {
  Low: "Response within 2 business days",
  Medium: "Response within 1 business day",
  High: "Response within 4 hours",
  Urgent: "Response within 1 hour · routed to on-call",
};

const SLOTS = ["2:30 PM", "3:00 PM", "4:15 PM", "5:00 PM", "6:30 PM"];

const CATEGORY_OPTIONS = [
  { value: "tech", label: "Technical issue / bug" },
  { value: "billing", label: "Billing & payments" },
  { value: "account", label: "Account access" },
  { value: "feature", label: "Feature request" },
];

const selectPopupProps = {
  getPopupContainer: () => document.body,
  styles: { popup: { zIndex: 2000 } },
};

const SupportIntakeModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const user = cache.getItem("user");
  const isLoggedIn = Boolean(user?.id || user?.email);

  const [mode, setMode] = useState("ticket");
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState("Medium");
  const [description, setDescription] = useState("");
  const [metaField, setMetaField] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [timing, setTiming] = useState("ASAP");
  const [slot, setSlot] = useState("");
  const [topic, setTopic] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(null);

  const [createTicket, { isLoading: ticketLoading }] = useCreateTicketMutation();
  const [createCallback, { isLoading: callbackLoading }] =
    useCreateCallbackMutation();

  useEffect(() => {
    if (!open) return;
    const u = cache.getItem("user");
    setMode("ticket");
    setCategory("");
    setPriority("Medium");
    setDescription("");
    setMetaField("");
    setPhone(u?.phoneNumber || "");
    setTopic("");
    setTiming("ASAP");
    setSlot("");
    setAttachments([]);
    setDone(null);
    setName(
      [u?.firstName, u?.lastName].filter(Boolean).join(" ") || ""
    );
    setEmail(u?.email || "");
  }, [open]);

  const metaLabel = useMemo(() => {
    switch (category) {
      case "tech":
        return "Which platform?";
      case "billing":
        return "Invoice or order number";
      case "account":
        return "Account email";
      case "feature":
        return "How important is this to you?";
      default:
        return null;
    }
  }, [category]);

  const metaOptions = useMemo(() => {
    if (category === "tech")
      return ["Web", "iOS", "Android", "Desktop"].map((v) => ({
        value: v,
        label: v,
      }));
    if (category === "feature")
      return [
        "Nice to have",
        "Would use often",
        "Blocking my work",
      ].map((v) => ({ value: v, label: v }));
    return null;
  }, [category]);

  const handleUpload = async (file) => {
    const err = validateSupportFile(file);
    if (err) {
      message.error(err);
      return Upload.LIST_IGNORE;
    }
    if (attachments.length >= 5) {
      message.error("Maximum 5 files");
      return Upload.LIST_IGNORE;
    }
    setUploading(true);
    try {
      const uploaded = await uploadSupportFileToS3(file);
      setAttachments((prev) => [...prev, uploaded]);
      message.success("File attached");
    } catch (e) {
      message.error(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
    return false;
  };

  const buildMetadata = () => {
    if (!category || !metaField) return undefined;
    const keys = {
      tech: "platform",
      billing: "invoiceOrOrder",
      account: "accountEmail",
      feature: "importance",
    };
    return { [keys[category]]: metaField };
  };

  const handleSubmitTicket = async () => {
    if (!category) return message.error("Select a category");
    if (!priority) return message.error("Select a priority");
    if (!description || description.trim().length < 10) {
      return message.error("Please describe the issue (at least 10 characters)");
    }
    if (!isLoggedIn) {
      if (!name.trim()) return message.error("Name is required");
      if (!email.trim()) return message.error("Email is required");
    }
    try {
      const result = await createTicket({
        category,
        priority,
        description: description.trim(),
        metadata: buildMetadata(),
        submitterName: name.trim() || undefined,
        submitterEmail: email.trim() || undefined,
        submitterPhone: phone.trim() || undefined,
        attachments: attachments.length ? attachments : undefined,
      }).unwrap();
      setDone({
        icon: "ticket",
        title: "Ticket created",
        msg: `Your ID is ${result.referenceNumber} — a confirmation is on its way.`,
        reference: result.referenceNumber,
      });
    } catch (err) {
      const msg =
        err?.data?.message ||
        (Array.isArray(err?.data?.message) ? err.data.message[0] : null) ||
        "Failed to create ticket";
      message.error(typeof msg === "string" ? msg : "Failed to create ticket");
    }
  };

  const handleSubmitCallback = async () => {
    if (!phone.trim()) return message.error("Phone number is required");
    if (timing === "SCHEDULED" && !slot) {
      return message.error("Pick a time slot");
    }
    if (!isLoggedIn && !email.trim()) {
      return message.error("Email is required for confirmation");
    }
    try {
      const result = await createCallback({
        phone: phone.trim(),
        timing,
        scheduledSlot: timing === "SCHEDULED" ? slot : undefined,
        topic: topic.trim() || undefined,
        submitterName: name.trim() || undefined,
        submitterEmail: email.trim() || undefined,
      }).unwrap();
      setDone({
        icon: "call",
        title: "Callback scheduled",
        msg: `Reference ${result.referenceNumber} — we'll call you ${result.timingLabel}.`,
        reference: result.referenceNumber,
      });
    } catch (err) {
      const msg = err?.data?.message || "Failed to request callback";
      message.error(typeof msg === "string" ? msg : "Failed to request callback");
    }
  };

  const loading = ticketLoading || callbackLoading || uploading;

  return (
    <StyledModal
      open={open}
      onCancel={onClose}
      footer={false}
      centered
      width={480}
      destroyOnClose
      zIndex={1100}
    >
      <TitleRow>
        <CustomerServiceOutlined style={{ fontSize: 22, color: "#ff6b35" }} />
        Get support
      </TitleRow>
      <Subtitle>Choose how you'd like to reach us.</Subtitle>

      {done ? (
        <DoneWrap>
          <CheckCircleOutlined style={{ fontSize: 36, color: "#1a7f4b" }} />
          <DoneTitle>{done.title}</DoneTitle>
          <DoneMsg>
            {done.msg.split(done.reference).map((part, i, arr) =>
              i < arr.length - 1 ? (
                <React.Fragment key={i}>
                  {part}
                  <code style={{ fontFamily: "monospace" }}>{done.reference}</code>
                </React.Fragment>
              ) : (
                part
              )
            )}
          </DoneMsg>
          {isLoggedIn && done.icon === "ticket" && (
            <SubmitButton
              type="button"
              onClick={() => {
                onClose();
                navigate("/profile?tab=mySupport");
              }}
            >
              View my tickets
            </SubmitButton>
          )}
          <button
            type="button"
            onClick={onClose}
            style={{
              marginTop: 12,
              background: "transparent",
              border: "none",
              color: "#666",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            Close
          </button>
        </DoneWrap>
      ) : (
        <>
          <ModeRow>
            <ModeButton
              type="button"
              $active={mode === "ticket"}
              onClick={() => setMode("ticket")}
            >
              <span className="mode-title">
                <FileTextOutlined /> Submit a ticket
              </span>
              <span className="mode-sub">Reply within a few hours</span>
            </ModeButton>
            <ModeButton
              type="button"
              $active={mode === "call"}
              onClick={() => setMode("call")}
            >
              <span className="mode-title">
                <PhoneOutlined /> Request a callback
              </span>
              <span className="mode-sub">We'll call you back</span>
            </ModeButton>
          </ModeRow>

          {!isLoggedIn && (
            <Form layout="vertical" style={{ marginBottom: 8 }}>
              <FieldLabel>Your name</FieldLabel>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
                style={{ marginBottom: 12 }}
              />
              <FieldLabel>Email</FieldLabel>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@company.com"
                style={{ marginBottom: 12 }}
              />
            </Form>
          )}

          {mode === "ticket" ? (
            <>
              <FieldLabel>What do you need help with?</FieldLabel>
              <Select
                style={{ width: "100%", marginBottom: 16 }}
                placeholder="Select a category…"
                value={category || undefined}
                onChange={(v) => {
                  setCategory(v);
                  setMetaField("");
                }}
                options={CATEGORY_OPTIONS}
                {...selectPopupProps}
              />

              {metaLabel && (
                <>
                  <FieldLabel>{metaLabel}</FieldLabel>
                  {metaOptions ? (
                    <Select
                      style={{ width: "100%", marginBottom: 16 }}
                      value={metaField || undefined}
                      onChange={setMetaField}
                      options={metaOptions}
                      placeholder="Select…"
                      {...selectPopupProps}
                    />
                  ) : (
                    <Input
                      value={metaField}
                      onChange={(e) => setMetaField(e.target.value)}
                      placeholder={
                        category === "billing" ? "INV-00000" : "name@company.com"
                      }
                      style={{ marginBottom: 16 }}
                    />
                  )}
                </>
              )}

              <FieldLabel>Priority</FieldLabel>
              <PriorityRow>
                {["Low", "Medium", "High", "Urgent"].map((p) => (
                  <ChipButton
                    key={p}
                    type="button"
                    $active={priority === p}
                    onClick={() => setPriority(p)}
                  >
                    {p}
                  </ChipButton>
                ))}
              </PriorityRow>
              <SlaText>{SLAS[priority]}</SlaText>

              <FieldLabel>Describe the issue</FieldLabel>
              <TextArea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What happened, and what did you expect?"
                rows={3}
                style={{ marginBottom: 16 }}
              />

              <FieldLabel>Screenshots or files (optional)</FieldLabel>
              <StyledUpload
                beforeUpload={handleUpload}
                fileList={attachments.map((a, i) => ({
                  uid: String(i),
                  name: a.fileName,
                  status: "done",
                  url: a.url,
                }))}
                onRemove={(file) => {
                  setAttachments((prev) =>
                    prev.filter((a) => a.fileName !== file.name)
                  );
                }}
                accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
                maxCount={5}
                listType="picture"
              >
                <SubmitButton
                  type="button"
                  style={{
                    background: "#fff",
                    color: "#333",
                    border: "1px dashed #ccc",
                    marginBottom: 16,
                  }}
                  disabled={uploading || attachments.length >= 5}
                >
                  {uploading ? "Uploading…" : "Add screenshots or files"}
                </SubmitButton>
              </StyledUpload>

              <SubmitButton
                type="button"
                disabled={loading}
                onClick={handleSubmitTicket}
              >
                {ticketLoading ? "Submitting…" : "Submit request"}
              </SubmitButton>
            </>
          ) : (
            <>
              {/* <AvailabilityBanner>
                <span className="dot" />
                Agents available · avg wait ~4 min
              </AvailabilityBanner> */}

              <FieldLabel>Phone number</FieldLabel>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+92 300 0000000"
                style={{ marginBottom: 16 }}
              />

              <FieldLabel>When should we call?</FieldLabel>
              <PriorityRow style={{ marginBottom: 16 }}>
                <ChipButton
                  type="button"
                  $active={timing === "ASAP"}
                  onClick={() => {
                    setTiming("ASAP");
                    setSlot("");
                  }}
                >
                  As soon as possible
                </ChipButton>
                <ChipButton
                  type="button"
                  $active={timing === "SCHEDULED"}
                  onClick={() => setTiming("SCHEDULED")}
                >
                  Pick a time
                </ChipButton>
              </PriorityRow>

              {timing === "SCHEDULED" && (
                <>
                  <FieldLabel>Available today</FieldLabel>
                  <SlotRow>
                    {SLOTS.map((s) => (
                      <ChipButton
                        key={s}
                        type="button"
                        $active={slot === s}
                        onClick={() => setSlot(s)}
                        style={{ flex: "unset", padding: "8px 12px" }}
                      >
                        {s}
                      </ChipButton>
                    ))}
                  </SlotRow>
                </>
              )}

              <FieldLabel>Topic (optional)</FieldLabel>
              <Input
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g. can't access my account"
                style={{ marginBottom: 20 }}
              />

              <SubmitButton
                type="button"
                disabled={loading}
                onClick={handleSubmitCallback}
              >
                {callbackLoading ? "Submitting…" : "Request callback"}
              </SubmitButton>
            </>
          )}
        </>
      )}
    </StyledModal>
  );
};

export default SupportIntakeModal;
